import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaChevronLeft, FaTimes } from 'react-icons/fa';
import '../../../styles/SkinShelfPanel.css';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { NAME_PAINT_SURFACES } from '../../../_helpers/namePaint.js';
import { nameInitials, skinTag } from '../../../_helpers/listenerCard.js';
import { SHELF_CATEGORIES, SHELF_PAGE_SIZE, localizeHowToEarn } from '../../../_helpers/skinShelf.js';
import { useSkinShelf } from '../../../hooks/useSkinShelf.js';
import { FX_ITEM_KEY, isFxSkin } from '../../../_helpers/fxItems.js';
import { BOOST_ANIMATION_SLOT } from '../../../_helpers/boostAnimations.js';
import { canBuyShopItem } from '../../../_helpers/skinShop.js';
import PaintedName from './PaintedName.jsx';
import { SkinPreview } from './ListenerProfileCard.jsx';
import FxTryOnPanel from './FxTryOnPanel.jsx';
import SkinAvailability from './SkinAvailability.jsx';
import { skinPurchaseError } from './SkinTryOnPanel.jsx';
import SkinPriceTag, { skinPriceLabel } from './SkinPriceTag.jsx';
import SkinTimeLimit, { useOfferClocks } from './SkinTimeLimit.jsx';
import '../../../styles/FxTryOnPanel.css';

/** A shelf skin's label: "Limited · #12 of 250", "Earned", "Standard" or its rarity. */
const ShelfTag = ({ owned }) => {
  const { t, format } = useLocale();
  const tag = skinTag(owned);
  if (!tag) return null;
  const text = tag.kind === 'limited' && tag.editionNumber
    ? t('stream.chat.card.tag.limitedEdition', { number: format.number(tag.editionNumber), size: format.number(tag.editionSize) })
    : t(`stream.chat.card.tag.${tag.kind}`);
  return <span className={`listener-card-tag listener-card-tag--${tag.kind}`} data-testid="skin-shelf-tag">{text}</span>;
};

const ACTION_ERRORS = { skin_not_owned: 'notOwned', skin_not_found: 'notFound', skin_type_mismatch: 'generic' };

/** "Vibe set" / "Boost animation" under an FX item's name. */
const FxType = ({ skin }) => {
  const { t } = useLocale();
  return <span className="skin-fx-type" data-testid="skin-shelf-fx-type">{t(`stream.fxItem.type.${FX_ITEM_KEY[skin.type]}`)}</span>;
};

/** A locked FX item's price: "Free" for $0, else the amount; null when it has none. */
const fxPrice = (item, t, format) => (item.forSale && item.priceCents != null
  ? skinPriceLabel(t, format, item.priceCents)
  : null);

/**
 * One FX item for sale that the listener doesn't own, under "Effects to
 * unlock": its price (SkinPriceTag; on sale the struck-through regular price,
 * Sale badge and its end, ENG-832), what's left, Preview and Get it. When its
 * sale ends, Get it turns off and `onOfferEnd()` reads the shelf again. A
 * time-limited item (ENG-834) shows when it stops being for sale
 * (SkinTimeLimit); when that passes, Get it turns off with "No longer
 * available" and the re-read drops the item.
 */
const LockedFxItem = ({ item, buying, onPreview, onGetIt, onOfferEnd }) => {
  const { t, format } = useLocale();
  const price = fxPrice(item, t, format);
  const sale = item.forSale ? item.sale ?? null : null;
  const until = item.forSale ? item.availableUntil ?? null : null;
  const clocks = useOfferClocks(sale, until, onOfferEnd);
  return (
    <li className="skin-shelf-item skin-shelf-item--locked" data-testid="skin-shelf-locked-item">
      <SkinPreview cardSkin={item} />
      <div className="skin-shelf-item-row">
        <span className="skin-shelf-item-name">{item.skin.name}</span>
        <span className="skin-fx-locked" data-testid="skin-shelf-locked-badge">{t('stream.fxItem.locked')}</span>
      </div>
      <FxType skin={item.skin} />
      {price && (
        <SkinPriceTag
          priceCents={item.priceCents}
          sale={sale}
          clock={clocks.saleClock}
          windowEnds={clocks.shared}
          className="skin-shelf-earnable-how"
          testId="skin-shelf-locked-price"
        />
      )}
      {!clocks.shared && !item.soldOut && (
        <SkinTimeLimit until={until} clock={clocks.windowClock} testId="skin-shelf-locked-time-limit" />
      )}
      <SkinAvailability offer={item} />
      <button
        type="button"
        className="skin-shelf-link skin-shelf-item-action"
        onClick={() => onPreview(item)}
        aria-label={t('stream.fxItem.previewLabel', { name: item.skin.name })}
        data-testid="skin-shelf-fx-preview"
      >
        {t('stream.fxItem.previewAction')}
      </button>
      {canBuyShopItem(item) && onGetIt && (
        <button
          type="button"
          className="skin-shelf-link skin-shelf-item-action"
          onClick={() => onGetIt(item)}
          disabled={buying || clocks.ended}
          aria-label={item.priceCents === 0
            ? t('shop.getItFreeLabel', { name: item.skin.name })
            : t('shop.getItLabel', { name: item.skin.name, price })}
          data-testid="skin-shelf-locked-get"
        >
          {item.priceCents === 0 ? t('shop.getItFree') : t('shop.getIt', { price })}
        </button>
      )}
    </li>
  );
};

/**
 * The previewed FX item as the latest shelf read has it: locked (with its
 * current price), owned (bought since), or as it was opened.
 */
const currentFxItem = (shelf, opened) => {
  if (!opened) return null;
  const locked = shelf?.locked.find((item) => item.id === opened.skin.id);
  if (locked) return locked;
  const owned = shelf?.skins.find((entry) => entry.skin.id === opened.skin.id);
  if (owned) return { skin: owned.skin, owned: true };
  return opened;
};

/**
 * The skin shelf (Skins, mockup 37, ENG-739): "See all" on this listener's own
 * profile card opens it in place of the chat and the Request / Boost / Tip
 * bar; the performer card stays. The listener header comes from that card
 * (`card`); everything below it, the filters and their counts included, from
 * `GET /me/skins/shelf` (ENG-740), one read per filter.
 *
 * All / Earned / Bought chips, a grid of skins (preview, name, WEARING badge,
 * rarity or edition label, Wear / Take off), "See N more", the earned skins
 * still to earn with their progress, and a toast for a skin the read just
 * granted. "Back to chat", × and Escape close it.
 *
 * Vibe sets and boost animations (ENG-816) are on it too: the owned ones in
 * the grid with their type, Wear / Take off (a worn free default has no Take
 * off: an empty slot plays it anyway) and Preview; the ones for sale the
 * listener does not own under "Effects to unlock", locked, with their price
 * ("Free" for $0), what's left or the Sold out badge (SkinAvailability,
 * ENG-833), Preview and Get it ("Get it free" for $0, ENG-827).
 * Preview opens FxTryOnPanel at the top of the shelf: a live preview on a
 * sample album card, on a stage of its own, never the queue's. Get it is
 * `onGetIt(item)`, which must buy through the page's useSkinPurchase
 * (`purchase` is its `{ state, error, priceChange }`), passing the item's
 * `priceCents`. A change of `refreshKey` (a purchase confirmed, or a price
 * that moved) re-reads the shelf; an open preview then shows the item as
 * read (owned, or at its new price). `onEquipChange(skin, worn)` hears each
 * Wear / Take off that succeeded (the page switches its vibe set live, ENG-812).
 */
const SkinShelfPanel = ({ card = null, onBack, onClose, purchase = null, onGetIt = null, refreshKey = 0, previewTiming = null, onEquipChange = null }) => {
  const { t, format } = useLocale();
  const shelfState = useSkinShelf(refreshKey, onEquipChange);
  const [fxOpened, setFxTry] = useState(null);
  const fxTry = currentFxItem(shelfState.shelf, fxOpened);
  const purchaseState = purchase?.state ?? 'idle';
  const buying = purchaseState === 'buying' || purchaseState === 'redirecting';
  const { category, status, shelf, error, earned, busySkinId, actionError, setCategory, retry, wear, takeOff, dismissEarned } = shelfState;
  const [expanded, setExpanded] = useState(false);
  const backRef = useRef(null);
  const location = useLocation();

  useEffect(() => { setExpanded(false); }, [category]);
  useEffect(() => { backRef.current?.focus(); }, []);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const name = card?.displayName ?? '';
  const subline = card?.regularAt
    ? t(`stream.chat.card.${card.regularAt.tier === 'superfan' ? 'superfanAt' : 'regularAt'}`, { band: card.regularAt.bandName })
    : null;

  // The skins shown are the current filter's; while another filter loads, the
  // last list stays rather than flashing empty.
  const skins = shelf?.skins ?? [];
  const visible = expanded ? skins : skins.slice(0, SHELF_PAGE_SIZE);
  const hidden = skins.length - visible.length;

  return (
    <section className="skin-shelf" aria-label={t('stream.shelf.label')} data-testid="skin-shelf">
      <div className="skin-shelf-top">
        <button ref={backRef} type="button" className="skin-shelf-back" onClick={() => onBack?.()}>
          <FaChevronLeft className="rtl-mirror" aria-hidden="true" /> {t('stream.shelf.backToChat')}
        </button>
        <button type="button" className="skin-shelf-close" onClick={() => onClose?.()} aria-label={t('stream.shelf.close')}>
          <FaTimes aria-hidden="true" />
        </button>
      </div>

      {card && (
        <div className="skin-shelf-header">
          {card.avatarUrl ? (
            <img className="listener-card-avatar skin-shelf-avatar" src={card.avatarUrl} alt="" />
          ) : (
            <span className="listener-card-avatar listener-card-avatar--initials skin-shelf-avatar" aria-hidden="true">{nameInitials(name)}</span>
          )}
          <div className="listener-card-identity">
            <PaintedName name={name} paint={card.namePaint} surface={NAME_PAINT_SURFACES.chat} className="skin-shelf-name" />
            {subline && <span className="listener-card-subline" data-testid="skin-shelf-subline">{subline}</span>}
          </div>
        </div>
      )}

      <div className="skin-shelf-filters" role="group" aria-label={t('stream.shelf.filters')}>
        {SHELF_CATEGORIES.map((key) => (
          <button
            key={key}
            type="button"
            className={`skin-shelf-chip${category === key ? ' skin-shelf-chip--active' : ''}`}
            aria-pressed={category === key}
            onClick={() => setCategory(key)}
            data-testid={`skin-shelf-filter-${key}`}
          >
            {shelf
              ? t(`stream.shelf.filter.${key}Count`, { count: format.number(shelf.counts[key]) })
              : t(`stream.shelf.filter.${key}`)}
          </button>
        ))}
      </div>

      {earned.length > 0 && (
        <div className="skin-shelf-toast" role="status" data-testid="skin-shelf-earned">
          <span>{t('stream.shelf.earned', { names: format.list(earned) })}</span>
          <button type="button" className="skin-shelf-toast-dismiss" onClick={dismissEarned} aria-label={t('stream.shelf.dismiss')}>
            <FaTimes aria-hidden="true" />
          </button>
        </div>
      )}

      {fxTry && (
        <FxTryOnPanel
          item={fxTry}
          purchase={purchase}
          onGetIt={(item) => onGetIt?.(item)}
          onClose={() => setFxTry(null)}
          timing={previewTiming}
          onOfferEnd={retry}
          showPhotosensitivityToggle={false}
        />
      )}

      {/* A purchase settling with no preview open (back from Checkout). */}
      {!fxTry && (purchase?.error || purchaseState === 'buying') && (
        <p className="skin-shelf-status" role="status" data-testid="skin-shelf-purchase">
          {purchase.error ? skinPurchaseError(t, format, purchase) : t('stream.tryOn.buying')}
        </p>
      )}

      {actionError && (
        <p className="skin-shelf-status" role="alert" data-testid="skin-shelf-action-error">
          {t(`stream.shelf.errors.${ACTION_ERRORS[actionError.code] ?? 'generic'}`)}
        </p>
      )}

      <div className="skin-shelf-body" aria-busy={status === 'loading'}>
        {status === 'loading' && !shelf && (
          <p className="skin-shelf-status" role="status">{t('stream.shelf.loading')}</p>
        )}

        {status === 'error' && (
          <div className="skin-shelf-status" role="alert">
            <span>{error?.status === 401 ? t('stream.shelf.signIn') : t('stream.shelf.error')}</span>
            {error?.status !== 401 && (
              <button type="button" className="skin-shelf-link" onClick={retry}>{t('stream.shelf.retry')}</button>
            )}
          </div>
        )}

        {shelf && status !== 'error' && (
          <>
            {skins.length === 0 ? (
              <p className="skin-shelf-status" data-testid="skin-shelf-empty">{t(`stream.shelf.empty.${category}`)}</p>
            ) : (
              <ul className="skin-shelf-grid" data-testid="skin-shelf-grid">
                {visible.map((owned) => (
                  <li key={owned.skin.id} className="skin-shelf-item" data-testid="skin-shelf-skin">
                    <SkinPreview cardSkin={owned} />
                    <div className="skin-shelf-item-row">
                      <span className="skin-shelf-item-name">{owned.skin.name}</span>
                      {owned.equipped && (
                        <span className="skin-shelf-wearing" data-testid="skin-shelf-wearing">{t('stream.shelf.wearing')}</span>
                      )}
                    </div>
                    {isFxSkin(owned.skin) && <FxType skin={owned.skin} />}
                    <ShelfTag owned={owned} />
                    {!(owned.equipped && owned.source === 'default' && isFxSkin(owned.skin)) && (() => {
                      // A boost is always worn (ENG-875): taking one off puts
                      // SongShake Boost back, so it reads "Reset to default".
                      const offKey = owned.skin.type === BOOST_ANIMATION_SLOT ? 'resetToDefault' : 'takeOff';
                      return (
                        <button
                          type="button"
                          className="skin-shelf-link skin-shelf-item-action"
                          disabled={busySkinId !== null}
                          onClick={() => (owned.equipped ? takeOff(owned) : wear(owned))}
                          aria-label={t(owned.equipped ? `stream.shelf.${offKey}Label` : 'stream.shelf.wearLabel', { name: owned.skin.name })}
                        >
                          {busySkinId === owned.skin.id
                            ? t('stream.shelf.saving')
                            : t(owned.equipped ? `stream.shelf.${offKey}` : 'stream.shelf.wear')}
                        </button>
                      );
                    })()}
                    {isFxSkin(owned.skin) && (
                      <button
                        type="button"
                        className="skin-shelf-link skin-shelf-item-action"
                        onClick={() => setFxTry({ skin: owned.skin, owned: true })}
                        aria-label={t('stream.fxItem.previewLabel', { name: owned.skin.name })}
                        data-testid="skin-shelf-fx-preview"
                      >
                        {t('stream.fxItem.previewAction')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {hidden > 0 && (
              <button type="button" className="skin-shelf-link skin-shelf-more" onClick={() => setExpanded(true)} data-testid="skin-shelf-more">
                {t('stream.shelf.seeMore', { count: hidden, number: format.number(hidden) })}
              </button>
            )}

            {shelf.locked.length > 0 && (
              <section className="skin-shelf-locked" data-testid="skin-shelf-locked">
                <h3 className="listener-card-heading">{t('stream.shelf.lockedHeading')}</h3>
                <ul className="skin-shelf-grid">
                  {shelf.locked.map((item) => (
                    <LockedFxItem
                      key={item.id}
                      item={item}
                      buying={buying}
                      onPreview={setFxTry}
                      onGetIt={onGetIt}
                      onOfferEnd={retry}
                    />
                  ))}
                </ul>
              </section>
            )}

            {shelf.earnable.length > 0 && (
              <section className="skin-shelf-earnable" data-testid="skin-shelf-earnable">
                <h3 className="listener-card-heading">{t('stream.shelf.toEarn')}</h3>
                <ul className="skin-shelf-earnable-list">
                  {shelf.earnable.map((item) => (
                    <li key={item.skin.id} className="skin-shelf-earnable-item" data-testid="skin-shelf-earnable-skin">
                      <SkinPreview cardSkin={{ skin: item.skin }} />
                      <div className="skin-shelf-earnable-text">
                        <span className="skin-shelf-item-name">{item.skin.name}</span>
                        {/* The Founding Listener cutoff in the viewer's time zone (ENG-876). */}
                        {item.howToEarn && (
                          <span className="skin-shelf-earnable-how" data-testid="skin-shelf-earnable-how">
                            {localizeHowToEarn(t, format, item.howToEarn, item.availableUntil)}
                          </span>
                        )}
                        {item.progress && (
                          <span className="skin-shelf-progress">
                            <progress max={item.progress.target} value={item.progress.current} aria-label={t('stream.shelf.progressLabel', { name: item.skin.name })} />
                            <span>{t('stream.shelf.progress', { current: format.number(item.progress.current), target: format.number(item.progress.target) })}</span>
                          </span>
                        )}
                        {item.availableUntil && (
                          <span className="skin-shelf-earnable-how">
                            {t('stream.shelf.availableUntil', { date: format.date(item.availableUntil, 'short') })}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>

      {/* The skins shop (ENG-823); Back there returns to this stream. */}
      <Link
        to="/shop"
        state={{ from: `${location.pathname}${location.search}` }}
        className="skin-shelf-link skin-shelf-shop"
        data-testid="skin-shelf-shop"
      >
        {t('stream.shelf.getMore')}
      </Link>
    </section>
  );
};

export default SkinShelfPanel;
